<template>
  <figure
    :class="[
      't-avatar',
      `t-avatar_size-${this.size}`,
    ]"
  >
    <img
      v-if="!shouldShowLetterAvatar"
      :src="src || fallbackSrc"
      :alt="`Avatar of ${name}`"
      @error="handleOnImageError"
      loading="lazy"
    >
    <svg
      v-else
      :style="svgStyle"
    >
      <text
        x="50%"
        y="50%"
        text-anchor="middle"
        dominant-baseline="central"
        :style="textStyle"
      >
        {{ avatarLetter }}
      </text>
    </svg>
  </figure>
</template>

<script>
export default {
  props: {
    src: {
      type: String,
      default: null,
    },
    fallbackSrc: {
      type: String,
      default: null,
    },
    name: {
      type: String,
      default: '',
    },
    size: {
      type: String,
      default: 'default',
      validator(value) {
        return [
          'small',
          'default',
          'medium',
          'large',
        ].includes(value);
      },
    },
  },
  data: () => ({
    srcLoadedWithErrors: false,
  }),
  computed: {
    shouldShowLetterAvatar() {
      return this.srcLoadedWithErrors || !(this.src || this.fallbackSrc);
    },
    avatarLetter() {
      return this.name?.[0]?.toUpperCase();
    },
    svgStyle() {
      // use hsl to define the background color.
      if (!this.avatarLetter) {
        return {
          border: '1px solid var(--gray-1)',
        };
      }

      const letterIndex = this.avatarLetter.charCodeAt() - 64;
      const hue = (360 / 26) * letterIndex;
      const backgroundColor = `hsl(${hue}, 68%, 48%)`;
      return {
        background: backgroundColor,
      };
    },
    textStyle() {
      return {
        fill: 'rgba(255, 255, 255, .7)',
        fontFamily: '\'Lucida Console\', Monaco, monospace',
        fontSize: `${this.size * 0.65}px`,
      };
    },
  },
  methods: {
    handleOnImageError(event) {
      const targetSrc = event.target.src;

      if (this.fallbackSrc && targetSrc !== this.fallbackSrc) {
        event.target.src = this.fallbackSrc;
        this.srcLoadedWithErrors = false;
      } else {
        this.srcLoadedWithErrors = true;
      }
    },
  },
};
</script>

<style
  lang="scss"
  scoped
>
@import "~@/assets/scss/mixins";

@mixin set-size($size) {
  width: $size;
  height: $size;
  font-size: ($size * 0.65);
}

$size-small: 24px;
$size-default: 32px;
$size-medium: 40px;
$size-large: 124px;


.t-avatar {
  display: inline-flex;
  box-sizing: border-box;

  img, svg {
    border-radius: 50%;
    height: inherit;
    width: inherit;
    pointer-events: none;
  }

  text {
    fill: rgba(255, 255, 255, .7);
    font-family: "Lucida Console", Monaco, monospace;
    font-size: inherit;
  }

  &_size {
    &-small {
      @include set-size($size-small);
    }

    &-default {
      @include set-size($size-default);
    }

    &-medium {
      @include set-size($size-medium);
    }

    &-large {
      @include set-size($size-large);
    }
  }
}
</style>>
